import React from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Divider,
  FormControl, List, MenuItem, Select,
} from '@mui/material';
import {
  Star as StarIcon, ArrowForward as ArrowForwardIcon,
  Lock as LockIcon, ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import FAQAccordion from '../../../FAQAccordion';
import ComparisonTable from '../../../ComparisonTable';
import TwoColumnComparisonTable from '../../../TwoColumnComparisonTable';
import FittableImage from '../../../FittableImage';
import CircularImageCropper from '../../../CircularImageCropper';
import ImageCropper from '../../../ImageCropper';
import Checklist from '../../../Checklist';
import CenteredVideoPlayer from '../../../CenteredVideoPlayer';
import ResizableButton from '../../../ResizableButton';
import InlineRowLayout from '../../../InlineRowLayout';
import ExtractedElementControls from '../../../ElementControls';
import { getPreset, getPresetFontFamilies } from '../../../../textStyle.js';
import { getListIcon } from '../../../../utils/offerListIcons';
import { ELEMENT_TYPES, LAYOUT_TYPES } from '../../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../../utils/offerLayoutConfigs';
import { calculateVideoAspectRatio } from '../../../../utils/offerPreviewElement';

export const renderVideoElements = (element, ctx) => {
  const {
    isEditing, isEditingField, handleDoubleClick,
    isElementSelected, getSelectionStyles, ElementControls,
    editingElement, editingField, tempValue, setTempValue, textEditorRef,
    selectedElement, selectedElementContext, selectedSubElement,
    iconSelectorOpen, setIconSelectorOpen, isBrandsPage, paginationEnabled,
    currentPageIndex, pageSettings, forceRender, rightPanelOpen,
    setSelectedElement, setSelectedElementContext, setSelectedSubElement,
    setTabValue, setRightPanelOpen, setPages, setElements,
    setInsertAfterElementId, setPromptDialogTitle, setPromptDialogMessage,
    setPromptDialogValue, setPromptAction, setShowPromptDialog,
    startInlineEdit, saveInlineEdit, cancelInlineEdit, handleKeyPress,
    handleResizeStart, findAndUpdateLayout, moveElement, generateId,
    deleteElement, deleteElementFromZone, updateElement, applyHighlight,
    handleDragOver, handleDragLeave, handleDrop,
    getZoneHeightStyle, getHeightModeText, toggleZoneHeight, getHeightTooltip,
    getExpandIcon, selectedVariantKey, setSelectedVariantKey, targetZone,
    isInZone, layoutId, zoneId, elementIndex,
    renderInlineEditableElement,
  } = ctx;

  switch (element.type) {
case ELEMENT_TYPES.VIDEO:
  // Calculate aspect ratio from element styles
  const canvasAspectRatio = calculateVideoAspectRatio(element);

  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 2, // Match preview margin-bottom (16px)
        mt: 0, // No top margin to match preview
        width: 'calc(100% + 32px)',
        maxWidth: 'calc(100% + 32px)',
        mx: -2, // Negative margin to counteract container padding (16px = 2 * 8px)
        ...getSelectionStyles(element.id)
      }}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(0); // Content tab for editing
        setRightPanelOpen(true);
      }}
      title="Click to select and edit video"
    >
      <ElementControls />
      {isEditingField('content') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          placeholder="YouTube embed URL (e.g., https://www.youtube.com/embed/VIDEO_ID)"
          sx={{ mb: 1 }}
          helperText="Enter YouTube embed URL or any video iframe src"
        />
      ) : (
        <Box
          sx={{
            textAlign: 'center',
            cursor: 'pointer',
            border: '2px dashed transparent',
            '&:hover': {
              opacity: 0.8,
              border: '2px dashed #1976d2'
            },
            p: 0, // Remove padding to match preview
            borderRadius: 0
          }}
          title="Click to select and edit video"
        >
          {(element.content || element.videoFile) ? (
            // If content exists, show either iframe or preview
            element.videoFile ? (
              // Show uploaded video with calculated aspect ratio
              // For brands page, always use full width regardless of preset
              <Box sx={{
                width: isBrandsPage ? '100%' : (element.styles?.width || '100%'),
                maxWidth: isBrandsPage ? '100%' : (element.styles?.maxWidth || '100%'),
                aspectRatio: canvasAspectRatio,
                position: 'relative',
                backgroundColor: '#000',
                borderRadius: 0,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 0, // Parent Box already handles the negative margin
                px: 0
              }}>
                <CenteredVideoPlayer
                  src={element.videoFile}
                  poster={element.thumbnailUrl || undefined}
                  style={{
                    width: '100%',
                    height: '100%',
                    maxWidth: '100%',
                    margin: 0,
                    padding: 0
                  }}
                  videoProps={{ title: 'Uploaded Video' }}
                />
                {/* Overlay for editing */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    pointerEvents: 'none',
                    zIndex: 3,
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                >
                  <Typography variant="body2" color="white" sx={{ fontWeight: 'bold' }}>
                    Click to select video
                  </Typography>
                </Box>
              </Box>
            ) : (element.content.includes('youtube.com') || element.content.includes('youtu.be') || element.content.includes('vimeo.com')) ? (
              <Box sx={{
                width: isBrandsPage ? '100%' : '100%',
                maxWidth: isBrandsPage ? '100%' : '100%',
                aspectRatio: canvasAspectRatio,
                mx: 0, // Parent Box already handles the negative margin
                px: 0,
                position: 'relative',
                backgroundColor: '#000',
                borderRadius: 0,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <iframe
                  src={element.content}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allowFullScreen
                  title="Video Preview"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    border: 'none',
                    borderRadius: 0,
                    margin: 0,
                    padding: 0
                  }}
                />
                {/* Overlay for editing */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                >
                  <Typography variant="body2" color="white" sx={{
                    textAlign: 'center',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    padding: 1,
                    borderRadius: 0
                  }}>
                    Click to select video
                  </Typography>
                </Box>
              </Box>
            ) : (
              // Non-video URL, show preview
              <Box sx={{
                width: element.styles?.width || '100%',
                height: element.styles?.height || '120px',
                backgroundColor: '#f44336',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 0,
                flexDirection: 'column',
                gap: 1
              }}>
                <Typography variant="body2" color="white" sx={{ fontWeight: 'bold' }}>
                  ⚠️ Invalid Video URL
                </Typography>
                <Typography variant="caption" color="white" sx={{ textAlign: 'center', px: 2 }}>
                  {element.content.substring(0, 40)}...
                </Typography>
                <Typography variant="caption" color="white">
                  Double-click to fix
                </Typography>
              </Box>
            )
          ) : (
            <Box sx={{
              width: element.styles?.width || '100%',
              aspectRatio: canvasAspectRatio,
              backgroundColor: '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 0,
              border: '2px dashed #1976d2',
              flexDirection: 'column',
              gap: 1
            }}>
              <Typography variant="h4" color="primary">
                🎥
              </Typography>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                No video uploaded
              </Typography>

            </Box>
          )}
        </Box>
      )}
    </Box>
  );

case ELEMENT_TYPES.VIDEO_WITH_BUTTON_OVERLAY: {
  const videoAspectRatio = calculateVideoAspectRatio(element);
  const [w, h] = videoAspectRatio.split('/').map(Number);
  const isVertical = w && h && h > w;

  let dynamicBottom = '24px';
  if (isVertical) {
    const ratioValue = h / w;
    if (ratioValue > 2) {
      dynamicBottom = '100px'; // 9:19 and taller
    } else if (ratioValue > 1.5) {
      dynamicBottom = '60px'; // 9:16
    } else if (ratioValue > 1) {
      dynamicBottom = '48px'; // Other vertical
    }
  }

  const getButtonPosition = (button) => {
    const presets = {
      'top-left': { top: '10px', left: '10px' },
      'top-right': { top: '10px', right: '10px' },
      'bottom-left': { bottom: '10px', left: '10px' },
      'bottom-right': { bottom: '10px', right: '10px' },
      'center': { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    };

    if (button.position === 'custom') {
      return {
        top: `${button.customY || 50}%`,
        left: `${button.customX || 50}%`,
        transform: 'translate(-50%, -50%)'
      };
    }

    return presets[button.position] || presets['bottom-right'];
  };

  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 2,
        width: 'calc(100% + 32px)',
        maxWidth: 'calc(100% + 32px)',
        mx: -2,
        ...getSelectionStyles(element.id)
      }}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(0);
        setRightPanelOpen(true);
      }}
    >
      <ElementControls />
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: videoAspectRatio,
          backgroundColor: '#000',
          borderRadius: 0,
          overflow: 'hidden'
        }}
      >
        {(element.content || element.videoFile) ? (
          element.videoFile ? (
            <CenteredVideoPlayer
              src={element.videoFile}
              poster={element.thumbnailUrl || undefined}
              fillParent={true}
              showControlsBar={false}
              style={{
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                margin: 0,
                padding: 0,
                objectFit: isVertical ? 'cover' : 'contain'
              }}
              videoProps={{ title: 'Uploaded Video' }}
            />
          ) : (element.content.includes('youtube.com') || element.content.includes('youtu.be') || element.content.includes('vimeo.com')) ? (
            <iframe
              src={element.content}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              title="Video Preview"
              style={{
                width: '100%',
                maxWidth: '100%',
                border: 'none',
                borderRadius: 0,
                margin: 0,
                padding: 0
              }}
            />
          ) : (
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#000',
              color: 'white'
            }}>
              <Typography variant="body2">No video uploaded</Typography>
            </Box>
          )
        ) : (
          <Box sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}>
            <Typography variant="h4" color="primary">🎥</Typography>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
              No video uploaded
            </Typography>
          </Box>
        )}

        {/* Overlay button at the bottom */}
        <Box
          sx={{
            position: 'absolute',
            bottom: dynamicBottom,
            left: (element.styles?.textAlign || 'center') === 'center' ? '50%' : ((element.styles?.textAlign || 'center') === 'right' ? 'auto' : '32px'),
            right: (element.styles?.textAlign || 'center') === 'right' ? '32px' : 'auto',
            transform: (element.styles?.textAlign || 'center') === 'center' ? 'translateX(-50%)' : 'none',
            width: '55%',
            zIndex: 20,
            display: 'flex',
            justifyContent: (element.styles?.textAlign || 'center') === 'center'
              ? 'center'
              : ((element.styles?.textAlign || 'center') === 'right' ? 'flex-end' : 'flex-start')
          }}
        >
          <button
            style={{
              ...(element.styles || {}),
              width: '100%',
              height: '44px',
              minHeight: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              textAlign: 'center',
              border: element.styles?.border || 'none'
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSelectedElement(element);
              setSelectedElementContext({ isInZone, layoutId, zoneId });
              setTabValue(0);
              setRightPanelOpen(true);
            }}
          >
            {element.content || 'Click Here'}
          </button>
        </Box>
      </Box>
    </Box>
  );
}

case ELEMENT_TYPES.MEDIA:
  return (
    <Box
      data-element-id={element.id}
      sx={{
        textAlign: 'center',
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mb: 0,
        ...getSelectionStyles(element.id)
      }}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(0); // Show Content tab for MEDIA
        setRightPanelOpen(true);
      }}
    >
      <ElementControls />
      {element.mediaUrl ? (
        // Show uploaded media
        element.mediaUrl.match(/\.(mp4|avi|mov|wmv|flv|webm)$/i) ? (
          <Box sx={{
            textAlign: 'center',
            mb: 0,
            position: 'relative',
            width: '100%',
            aspectRatio: element.styles?.aspectRatio || 'auto',
            overflow: 'hidden',
            borderRadius: '8px'
          }}>
            <video
              src={element.mediaUrl}
              controls
              poster={element.thumbnailUrl || undefined}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </Box>
        ) : (
          <Box sx={{
            textAlign: 'center',
            mb: 0,
            position: 'relative'
          }}>
            <img
              src={element.mediaUrl}
              alt="Uploaded media"
              style={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '8px',
                display: 'block',
                margin: '0 auto'
              }}
            />
          </Box>
        )
      ) : (
        // Show placeholder
        <Box sx={{
          textAlign: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          mb: 0,
          p: 3,
          border: '2px dashed #ccc',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#e8e8e8',
            borderColor: '#1976d2'
          }
        }}>
          <Typography variant="body2" color="text.secondary">
            📁 No media uploaded. Click to upload image or video.
          </Typography>
        </Box>
      )}
    </Box>
  );
    default:
      return null;
  }
};
